import { ReactElement, useState, MouseEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { AngleDownIcon } from 'components/Icons/AngleDown'
import { VisibilityOutlineIcon } from 'components/Icons/VisibilityOutlineIcon'
import { VisibilityOffOutlineIcon } from 'components/Icons/VisibilityOffOutlineIcon'
import { IRoutesPlanningCollection } from 'features/RoutesPlanning/collections'
import { IRoutePlanningModel } from 'features/RoutesPlanning/models'
import { rgbToHex } from 'utils/style'

const StopsList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StopItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StopIndex = styled.div<{ color: string; bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
  border-radius: 50%;
  background-color: ${({ bgColor }): string => bgColor};
  color: ${({ color }): string => color};
  font-size: 12px;
  font-weight: 500;
`

const StopName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #28265d;
`

interface IRouteStopsProps {
  stops: IRoutePlanningModel['stops']
  rgbColor: string
}

const RouteStops = ({ stops, rgbColor }: IRouteStopsProps): ReactElement => {
  return (
    <StopsList>
      {stops.map((stop, stopIndex): ReactElement => {
        const isDeparture = stopIndex === 0
        const isArrival = stopIndex === stops.length - 1

        return (
          <StopItem key={stop.id}>
            <StopIndex color={rgbColor} bgColor={`${rgbToHex(rgbColor)}20`}>
              {isDeparture && 'D'}
              {isArrival && 'A'}
              {!isArrival && !isDeparture && stopIndex}
            </StopIndex>
            <StopName>{stop.name}</StopName>
          </StopItem>
        )
      })}
    </StopsList>
  )
}

const RouteItemContainer = styled.li`
  padding: 0px;
  border-radius: 8px;
  border: 1px solid #eae9e9;
  display: grid;
  grid-template-columns: 7px 1fr;
  overflow: hidden;
`

const RouteItemFlag = styled.div<{ color: string }>`
  background-color: ${({ color }): string => color};
  width: 100%;
  height: 100%;
`

const RouteItemTitle = styled.div`
  padding: 16px;
  display: grid;
  gap: 8px;
  align-items: center;
  grid-template-columns: 1fr min-content min-content min-content;
  cursor: pointer;
`

const RouteName = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #28265d;
`

const RouteServices = styled.span<{ color: string; bgColor: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ color }): string => color};
  padding: 4px 8px;
  border-radius: 24px;
  background-color: ${({ bgColor }): string => bgColor};
  white-space: nowrap;
`

const VisibilityContainer = styled.span`
  display: flex;
  cursor: pointer;
  padding: 16px 8px;
  margin: -16px -8px;
`

const StyledAngleDownIcon = styled(AngleDownIcon)<{ isOpen?: boolean }>`
  fill: #717171;
  height: 6px !important;
  transform: ${({ isOpen }): string => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: 0.2s;
  width: 10px !important;
`

const Split = styled.div`
  width: calc(100% - 32px);
  margin: auto;
  background-color: #eae9e9;
  height: 1px;
`

const RouteStopsContainer = styled.div`
  padding: 16px;
`

interface IRouteItemProps {
  route: IRoutePlanningModel
  isVisible: boolean
  onToggleVisibility: (routeId: string) => void
}

const RouteItem = ({ route, isVisible, onToggleVisibility }: IRouteItemProps): ReactElement => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  const toggleRouteDetails = (): void => {
    setIsDetailsOpen((isOpen) => !isOpen)
  }

  const handleToggleVisibility = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    onToggleVisibility(route.id)
  }

  return (
    <RouteItemContainer>
      <RouteItemFlag color={route.rgbColor} />
      <div>
        <RouteItemTitle onClick={toggleRouteDetails}>
          <RouteName>{route.name}</RouteName>
          <RouteServices color={route.rgbColor} bgColor={`${rgbToHex(route.rgbColor)}20`}>
            <FormattedMessage
              id="routesPlanning.list.routeServices"
              defaultMessage="{servicesCount} Services"
              values={{ servicesCount: route.services.length }}
            />
          </RouteServices>
          <VisibilityContainer onClick={handleToggleVisibility}>
            {isVisible ? <VisibilityOutlineIcon /> : <VisibilityOffOutlineIcon />}
          </VisibilityContainer>
          <StyledAngleDownIcon isOpen={isDetailsOpen} />
        </RouteItemTitle>
        {isDetailsOpen && (
          <>
            <Split />
            <RouteStopsContainer>
              <RouteStops stops={route.stops} rgbColor={route.rgbColor} />
            </RouteStopsContainer>
          </>
        )}
      </div>
    </RouteItemContainer>
  )
}

const RoutesList = styled.ul`
  list-style-type: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: fit-content;
`

interface IRoutesPlanningListProps {
  routes: IRoutesPlanningCollection
  visibleRoutesIds: string[]
  setVisibleRoutesIds: (ids: string[]) => void
}

export const RoutesPlanningList = ({
  routes,
  visibleRoutesIds,
  setVisibleRoutesIds
}: IRoutesPlanningListProps): ReactElement => {
  return (
    <RoutesList>
      {routes.items.map((route) => {
        const handleToggleVisibility = (routeId: string): void => {
          if (visibleRoutesIds.includes(routeId)) {
            setVisibleRoutesIds(visibleRoutesIds.filter((id) => id !== routeId))
          } else {
            setVisibleRoutesIds([...visibleRoutesIds, routeId])
          }
        }

        return (
          <RouteItem
            key={route.id}
            route={route}
            isVisible={visibleRoutesIds.includes(route.id)}
            onToggleVisibility={handleToggleVisibility}
          />
        )
      })}
    </RoutesList>
  )
}
